// src/pages/Success.js
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { getOrderById } from '../db';
import { Box, Button, Typography } from '@mui/material';
import StatusPage from '../components/StatusPage';
import { useSeo } from '../seo';

// Yoco confirms payment by webhook, not by this redirect, so the order may take
// a moment to flip to paid. Poll for a little while before giving up.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

/** Which badge each outcome wears. The copy below stays as it was written. */
const TONE_BY_STATUS = {
  pending: 'pending',
  confirmed: 'success',
  mismatch: 'error',
  timeout: 'warning',
  missing: 'error',
};

const Success = () => {
  useSeo({ title: 'Order status | Crestancia', path: '/success', noindex: true });

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order');

  const [status, setStatus] = useState('pending');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setStatus('missing');
      return;
    }

    let cancelled = false;
    let timer;
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      if (cancelled) return;
      try {
        const found = await getOrderById(orderId);
        if (cancelled) return;

        if (found?.paid) {
          setOrder(found);
          setStatus(found.status === 'amount_mismatch' ? 'mismatch' : 'confirmed');
          localStorage.removeItem('cart');
          // The header badge listens for this. Without it the bought-and-paid
          // cart keeps showing its old count until the next full page load.
          window.dispatchEvent(new Event('cartUpdated'));
          return;
        }
      } catch (err) {
        console.error('Error checking order status:', err);
      }

      if (Date.now() >= deadline) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  const heading =
    status === 'confirmed'
      ? 'Payment Successful'
      : status === 'pending'
      ? 'Confirming Your Payment'
      : 'Order Status';

  const homeButton = (
    <Button
      component={RouterLink}
      to="/home"
      variant="contained"
      disableElevation
      sx={{ px: 4, py: 1.4, borderRadius: 999, fontWeight: 700, textTransform: 'none' }}
    >
      Return home
    </Button>
  );

  // Offered alongside it once there is an order to look at: the orders page is
  // where the invoice and a second copy of the confirmation email live.
  const ordersButton = (
    <Button
      component={RouterLink}
      to="/orders"
      variant="outlined"
      sx={{ px: 4, py: 1.4, borderRadius: 999, fontWeight: 700, textTransform: 'none' }}
    >
      View your orders
    </Button>
  );

  return (
    <StatusPage
      tone={TONE_BY_STATUS[status] || 'success'}
      title={heading}
      actions={
        status === 'pending' ? null : status === 'confirmed' ? (
          <>
            {ordersButton}
            {homeButton}
          </>
        ) : (
          homeButton
        )
      }
    >
      {status === 'pending' && (
        <Typography>
          We're confirming your payment with Yoco. This usually takes a few seconds — please
          don't close this page.
        </Typography>
      )}

      {status === 'confirmed' && (
        <>
          <Typography sx={{ mb: 2 }}>
            Your payment was successful and your order has been recorded.
          </Typography>
          {order?.orderNum && (
            <Box
              sx={{
                display: 'inline-block',
                px: 2.5,
                py: 1.25,
                mb: 2,
                borderRadius: 2,
                backgroundColor: 'action.hover',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Order number
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary' }}>
                {order.orderNum}
              </Typography>
            </Box>
          )}
          <Typography variant="body2">
            {order?.email_sent
              ? 'A confirmation email has been sent to your email address.'
              : "We're finishing up your confirmation email. If it doesn't arrive shortly, please contact a member of our team with your order number."}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Your tax invoice is ready whenever you need it — you'll find it, and a way to have
            the confirmation sent again, under your orders.
          </Typography>
        </>
      )}

      {status === 'mismatch' && (
        <Typography sx={{ color: 'error.main' }}>
          We received your payment but the amount did not match the order. Our team has been
          notified and will contact you shortly
          {order?.orderNum ? ` about order ${order.orderNum}` : ''}.
        </Typography>
      )}

      {status === 'timeout' && (
        <Typography>
          Your payment is still being confirmed. If money left your account, your order is safe —
          it will appear once Yoco finishes processing. Please contact us if you don't receive a
          confirmation email within the hour.
        </Typography>
      )}

      {status === 'missing' && (
        <Typography sx={{ color: 'error.main' }}>
          No order reference was found in this link, so we can't show your order status here. If
          you completed a payment, your confirmation email will follow.
        </Typography>
      )}
    </StatusPage>
  );
};

export default Success;
